
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Layout from '../../../../../components/docs/Layout';
import styles from '../../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'Consent API',
  layout: 'guide',
  description: 'Read and revoke FDX consent grants programmatically',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Image = makeShortcode("Image");
const Schema = makeShortcode("Schema");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Consent API (beta)' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "overview"
      }}>{`Overview`}</h2>
      <p>{`Use the Consent API to build a consumer-facing consent portal, to answer support requests about a customer's connections, and to revoke access when a customer withdraws consent. `}</p>
      <p>{`The Consent API represents each of your customers' authorizations as an FDX `}<strong parentName="p">{`consent grant`}</strong>{`: a record of which application has access, which of the customer's resources it can read, which data clusters are in scope, and how long that access lasts.`}</p>
      <Callout mdxType="Callout">
        <p>{`The Consent API is currently in beta. To request access, contact Plaid solutions engineering. Note that the no-code Dashboard does not currently support the Consent API; if using both surfaces, you may see inconsistent data across the two.`}</p>
      </Callout>
      <p>{`For conceptual background on authorization records, or to learn how to manage consent via the Dashboard instead of via API, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/consent-management"
        }}>{`Consent management`}</a>{`.`}</p>
      <h2 {...{
        "id": "customer-identifiers"
      }}>{`Customer identifiers`}</h2>
      <p>{`Every consent grant is scoped to a `}<inlineCode parentName="p">{`customerId`}</inlineCode>{`, which must be the same persistent, unique identifier you use as the `}<inlineCode parentName="p">{`sub`}</inlineCode>{` claim in your OIDC ID token. See `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/oauth-server-setup/#unique-user-identifier-consistency-key"
        }}>{`Unique user identifier (consistency key)`}</a>{`.`}</p>
      <p>{`If you are a platform or gateway serving multiple institutions, Plaid assumes `}<inlineCode parentName="p">{`customerId`}</inlineCode>{` is unique across all of them. If you reuse a customer identifier across institutions, contact your Plaid solutions engineering team before you integrate.`}</p>
      <h2 {...{
        "id": "data-clusters"
      }}>{`Data clusters`}</h2>
      <p>{`A consent grant covers a set of resources, each with a list of FDX data clusters. `}<inlineCode parentName="p">{`resources`}</inlineCode>{` is omitted entirely when a grant covers none. `}<inlineCode parentName="p">{`resourceType`}</inlineCode>{` is one of `}<inlineCode parentName="p">{`ACCOUNT`}</inlineCode>{`, `}<inlineCode parentName="p">{`CUSTOMER`}</inlineCode>{`, or `}<inlineCode parentName="p">{`DOCUMENT`}</inlineCode>{`, and the clusters Plaid may request are:`}</p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Data cluster`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Includes`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ACCOUNT_BASIC`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Account name, type, and masked account number`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ACCOUNT_DETAILED`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Everything in `}<inlineCode parentName="td">{`ACCOUNT_BASIC`}</inlineCode>{`, plus credit limits, due dates, and interest rates`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`BALANCES`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Current and available balances`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`PAYMENT_SUPPORT`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Account and routing numbers and other stored payment methods`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`SCHEDULED_PAYMENTS`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Future-dated single and recurring payments, with amounts and frequency`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TRANSACTIONS`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Transaction amounts, dates, descriptions, and merchants`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`STATEMENTS`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`PDF statements`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`INVESTMENTS`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Investment holdings, securities details, quantities, and market values`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CUSTOMER_CONTACT`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Account owner name, email, phone, and postal address`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TAX`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Tax forms such as 1099s and W-2s`}</td>
          </tr>
        </tbody>
      </table>
      <p>{`Plaid uses a different, consumer-facing taxonomy for these clusters in the Link flow. Contact your Plaid solutions engineering team if you need the mapping between the two.`}</p>
      <Callout mdxType="Callout">
        <p>{`Plaid does not use the `}<inlineCode parentName="p">{`BILLS`}</inlineCode>{`, `}<inlineCode parentName="p">{`CUSTOMER_PERSONAL`}</inlineCode>{`, `}<inlineCode parentName="p">{`IMAGES`}</inlineCode>{`, and `}<inlineCode parentName="p">{`REWARDS`}</inlineCode>{` clusters, so they will not appear on a consent grant Plaid brokers. The `}<inlineCode parentName="p">{`FDXDataCluster`}</inlineCode>{` enum also defines `}<inlineCode parentName="p">{`ACCOUNT_PAYMENTS`}</inlineCode>{` and `}<inlineCode parentName="p">{`NOTIFICATIONS`}</inlineCode>{`.`}</p>
      </Callout>
      <h2 {...{
        "id": "revocation-and-ecosystem-sync"
      }}>{`Revocation and ecosystem sync`}</h2>
      <p>{`If you offer users the ability to revoke access to connected apps (for example, via a consumer-facing consent portal), you must keep the ecosystem in sync. When a user revokes access on your side, Plaid and the downstream app need to know immediately.`}</p>
      <p><strong parentName="p">{`How it works:`}</strong></p>
      <ol>
        <li parentName="ol"><strong parentName="li">{`User revokes access on your domain:`}</strong>{` User visits your consent portal and disconnects an app`}</li>
        <li parentName="ol"><strong parentName="li">{`Call `}<a parentName="strong" {...{
              "href": "#revoke-a-consent-grant"
            }}><inlineCode parentName="a">{`PUT /fdx/consents/{consentId}/revocation`}</inlineCode></a>{`:`}</strong>{` Your system calls Plaid's revocation endpoint`}</li>
        <li parentName="ol"><strong parentName="li">{`Plaid notifies the app:`}</strong>{` Plaid sends a webhook to the disconnected app`}</li>
        <li parentName="ol"><strong parentName="li">{`Plaid severs the connection:`}</strong>{` Plaid blocks the app from accessing data`}</li>
        <li parentName="ol"><strong parentName="li">{`App stops requesting data:`}</strong>{` The app knows the user revoked access and stops making requests`}</li>
      </ol>
      <p>{`This keeps revocation instant across all parties. Without it, the app won't know that consent has been revoked until the next time it attempts to actively fetch data from the connection. `}</p>
      <p>{`Plaid notifies you of these events in real time via webhooks; see `}<a parentName="p" {...{
          "href": "#webhooks"
        }}>{`Webhooks`}</a>{` below.`}</p>
      <h2 {...{
        "id": "webhooks"
      }}>{`Webhooks`}</h2>
      <p>{`Plaid can optionally send webhooks to you when authorization events occur. Single-institution accounts can configure these in the Data Partner Dashboard via the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/developers/webhooks"
        }}>{`Developers > Webhooks`}</a>{` page. Platform accounts should reach out to your Plaid contact to have these enabled. `}</p>
      <Image src='/assets/img/core-exchange/permissions-manager/webhooks.png' alt='Webhook configuration interface in the Data Partner Dashboard' caption='Configure webhooks for real-time connection and disconnection alerts' expandable={true} mdxType="Image" />
      <p>{`Plaid sends two webhooks: `}<a parentName="p" {...{
          "href": "#authorization_granted"
        }}><inlineCode parentName="a">{`AUTHORIZATION_GRANTED`}</inlineCode></a>{` and `}<a parentName="p" {...{
          "href": "#consent_revoked"
        }}><inlineCode parentName="a">{`CONSENT_REVOKED`}</inlineCode></a>{`.`}</p>
      <p>{`Each webhook is an `}<a parentName="p" {...{
          "href": "https://financialdataexchange.org/"
        }}>{`FDX event notification`}</a>{`: a `}<inlineCode parentName="p">{`category`}</inlineCode>{`/`}<inlineCode parentName="p">{`type`}</inlineCode>{` pair identifies the event, and `}<inlineCode parentName="p">{`notificationPayload.customFields`}</inlineCode>{` carries the details as name/value pairs.`}</p>
      <h3 {...{
        "id": "authorization_granted"
      }}><inlineCode parentName="h3">{`AUTHORIZATION_GRANTED`}</inlineCode></h3>
      <p>{`Notifies you every time a user connects a new app via Plaid. While your system will already know about new OAuth authorizations, this webhook also alerts you to connections created via the returning user experience, where the user doesn't go through your OAuth flow and this webhook is your only real-time signal that a new connection was created.`}</p>
      <p><strong parentName="p">{`When it fires:`}</strong>{` After a user successfully authorizes a new app.`}</p>
      <p><strong parentName="p">{`Payload includes:`}</strong>{` The customer (`}<inlineCode parentName="p">{`user_identifier`}</inlineCode>{`) and app details (`}<inlineCode parentName="p">{`application_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`application_name`}</inlineCode>{`).`}</p>
      <p><strong parentName="p">{`What to do:`}</strong>{` Record the new connection. The webhook identifies the user and the app, but not the accounts or data clusters they authorized, so treat it as a signal to fetch the full consent grant from `}<a parentName="p" {...{
          "href": "#list-a-customers-consent-grants"
        }}><inlineCode parentName="a">{`GET /fdx/consents`}</inlineCode></a>{` rather than as the authorization record itself. If you offer a consent portal, display this new connection so users can view and manage it.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# AUTHORIZATION_GRANTED example",
          "#": true,
          "AUTHORIZATION_GRANTED": true,
          "example": true
        }}>{`{
  "notificationId": "b3f1c4d2-8a5e-4b6a-9c1f-2e7d5a8b3c9d",
  "sentOn": "2026-08-05T14:32:00Z",
  "category": "AUTHORIZATION",
  "type": "AUTHORIZATION_GRANTED",
  "publisher": { "name": "Plaid", "type": "DATA_ACCESS_PLATFORM" },
  "notificationPayload": {
    "id": "consentapi-example-1",
    "idType": "CONSENT",
    "customFields": [
      { "name": "user_identifier", "value": "consentapi-example-1" },
      { "name": "application_id", "value": "36b78a27-ebbb-417b-a7b7-351734d2f8bd" },
      { "name": "application_name", "value": "Incremental Investing" }
    ]
  }
}
`}</code></pre>
      <h3 {...{
        "id": "consent_revoked"
      }}><inlineCode parentName="h3">{`CONSENT_REVOKED`}</inlineCode></h3>
      <p>{`Notifies you when a user revokes access to an app, either through the third-party app itself or via `}<a parentName="p" {...{
          "href": "https://my.plaid.com"
        }}>{`my.plaid.com`}</a>{` (Plaid Portal). If you offer a consent portal, you need this webhook to catch the revocations that happen elsewhere; otherwise your portal will drift out of sync when users disconnect through my.plaid.com or the app itself instead of through you.`}</p>
      <p><strong parentName="p">{`When it fires:`}</strong>{` After a user or app revokes authorization.`}</p>
      <p><strong parentName="p">{`Payload includes:`}</strong>{` The customer (`}<inlineCode parentName="p">{`user_identifier`}</inlineCode>{`), app details, and who initiated the revocation (`}<inlineCode parentName="p">{`initiator`}</inlineCode>{`: `}<inlineCode parentName="p">{`DATA_ACCESS_PLATFORM`}</inlineCode>{`, `}<inlineCode parentName="p">{`INDIVIDUAL`}</inlineCode>{`, or `}<inlineCode parentName="p">{`DATA_RECIPIENT`}</inlineCode>{`).`}</p>
      <p><strong parentName="p">{`What to do:`}</strong>{` Update your authorization records to mark the connection as revoked. If you offer a consent portal, update the display to show that the connection is no longer active.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# CONSENT_REVOKED example",
          "#": true,
          "CONSENT_REVOKED": true,
          "example": true
        }}>{`{
  "notificationId": "e7a2d9f1-3b6c-4a8e-9d5f-1c8b4e2a7d3f",
  "sentOn": "2026-08-05T14:35:00Z",
  "category": "CONSENT",
  "type": "CONSENT_REVOKED",
  "publisher": { "name": "Plaid", "type": "DATA_ACCESS_PLATFORM" },
  "notificationPayload": {
    "id": "consentapi-example-1",
    "idType": "CONSENT",
    "customFields": [
      { "name": "user_identifier", "value": "consentapi-example-1" },
      { "name": "application_id", "value": "36b78a27-ebbb-417b-a7b7-351734d2f8bd" },
      { "name": "application_name", "value": "Incremental Investing" },
      { "name": "initiator", "value": "INDIVIDUAL" }
    ]
  }
}
`}</code></pre>
      <h2 {...{
        "id": "best-practices"
      }}>{`Best practices`}</h2>
      <ul>
        <li parentName="ul">{`Implement the `}<inlineCode parentName="li">{`CONSENT_REVOKED`}</inlineCode>{` webhook if you offer a consent portal`}</li>
        <li parentName="ul">{`Use `}<a parentName="li" {...{
            "href": "#revoke-a-consent-grant"
          }}><inlineCode parentName="a">{`PUT /fdx/consents/{consentId}/revocation`}</inlineCode></a>{` to keep the ecosystem in sync during revocations`}</li>
        <li parentName="ul">{`Set refresh token expiration to 13+ months (allows buffer for reauthorization)`}</li>
        <li parentName="ul">{`Direct users to `}<a parentName="li" {...{
            "href": "https://my.plaid.com"
          }}>{`my.plaid.com`}</a>{` for self-service connection management`}</li>
      </ul>
      <h2 {...{
        "id": "authentication"
      }}>{`Authentication`}</h2>
      <p>{`Find your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` on the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/developers/keys"
        }}>{`Developer > Keys`}</a>{` tab of the Data Partner Dashboard and include both as the `}<inlineCode parentName="p">{`PLAID-CLIENT-ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`PLAID-SECRET`}</inlineCode>{` headers, or as `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` in the request body. All requests must be made over HTTPS.`}</p>
      <h2 {...{
        "id": "endpoints"
      }}>{`Endpoints`}</h2>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Endpoint`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Functionality`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "#list-a-customers-consent-grants"
              }}><inlineCode parentName="a">{`GET /fdx/consents`}</inlineCode></a></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`List a customer's consent grants`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "#get-a-single-consent-grant"
              }}><inlineCode parentName="a">{`GET /fdx/consents/{consentId}`}</inlineCode></a></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Get a single consent grant`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "#revoke-a-consent-grant"
              }}><inlineCode parentName="a">{`PUT /fdx/consents/{consentId}/revocation`}</inlineCode></a></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Revoke a consent grant`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "#retrieve-a-consent-grants-revocation-history"
              }}><inlineCode parentName="a">{`GET /fdx/consents/{consentId}/revocation`}</inlineCode></a></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Get a consent grant's revocation history`}</td>
          </tr>
        </tbody>
      </table>
      <h3 {...{
        "id": "list-a-customers-consent-grants"
      }}>{`List a customer's consent grants`}</h3>
      <h4 {...{
        "id": "get-fdxconsents"
      }}><inlineCode parentName="h4">{`GET /fdx/consents`}</inlineCode></h4>
      <p>{`Fetch this list each time a customer opens your consent portal rather than serving a cached copy, so that revocations made elsewhere in the ecosystem are reflected immediately. Omit `}<inlineCode parentName="p">{`status`}</inlineCode>{` to return grants in every state, which is useful for an audit view.`}</p>
      <Schema method='GET' route='/fdx/consents' type='request' mdxType="Schema">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X GET 'https://production.plaid.com/fdx/consents?customerId=<customer_id>&status=ACTIVE' \\
  -H 'PLAID-CLIENT-ID: <client_id>' \\
  -H 'PLAID-SECRET: <secret>'
`}</code></pre>
      </Schema>
      <Schema method='GET' route='/fdx/consents' type='response' mdxType="Schema" />
      <h3 {...{
        "id": "get-a-single-consent-grant"
      }}>{`Get a single consent grant`}</h3>
      <h4 {...{
        "id": "get-fdxconsentsconsentid"
      }}><inlineCode parentName="h4">{`GET /fdx/consents/{consentId}`}</inlineCode></h4>
      <p>{`Use the `}<inlineCode parentName="p">{`parties`}</inlineCode>{` array to identify the application: the entry with `}<inlineCode parentName="p">{`type`}</inlineCode>{` of `}<inlineCode parentName="p">{`DATA_RECIPIENT`}</inlineCode>{` is the connected app, `}<inlineCode parentName="p">{`DATA_PROVIDER`}</inlineCode>{` is you, and `}<inlineCode parentName="p">{`DATA_ACCESS_PLATFORM`}</inlineCode>{` is Plaid.`}</p>
      <p>{`If you have an existing live integration and need the `}<inlineCode parentName="p">{`consentId`}</inlineCode>{` values for connections that predate it, contact your Plaid solutions engineering team to coordinate a backfill.`}</p>
      <Schema method='GET' route='/fdx/consents/{consentId}' type='request' mdxType="Schema">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X GET 'https://production.plaid.com/fdx/consents/<consent_id>' \\
  -H 'PLAID-CLIENT-ID: <client_id>' \\
  -H 'PLAID-SECRET: <secret>'
`}</code></pre>
      </Schema>
      <Schema method='GET' route='/fdx/consents/{consentId}' type='response' mdxType="Schema" />
      <h3 {...{
        "id": "revoke-a-consent-grant"
      }}>{`Revoke a consent grant`}</h3>
      <h4 {...{
        "id": "put-fdxconsentsconsentidrevocation"
      }}><inlineCode parentName="h4">{`PUT /fdx/consents/{consentId}/revocation`}</inlineCode></h4>
      <p>{`Call this endpoint as soon as a customer disconnects an application in your portal, or when your own risk or compliance systems terminate access. Plaid then notifies the application and stops serving it data, which is what keeps your portal, Plaid, and the application in sync. See `}<a parentName="p" {...{
          "href": "#revocation-and-ecosystem-sync"
        }}>{`Revocation and ecosystem sync`}</a>{` for what happens if you skip this call.`}</p>
      <p>{`Set `}<inlineCode parentName="p">{`initiator`}</inlineCode>{` to the party that triggered the revocation, usually `}<inlineCode parentName="p">{`DATA_PROVIDER`}</inlineCode>{`. Set `}<inlineCode parentName="p">{`reason`}</inlineCode>{` to `}<inlineCode parentName="p">{`USER_ACTION`}</inlineCode>{` when a customer disconnects the app themselves, or `}<inlineCode parentName="p">{`BUSINESS_RULE`}</inlineCode>{` or `}<inlineCode parentName="p">{`SECURITY_EVENT`}</inlineCode>{` when you revoke on your own initiative. If you pass `}<inlineCode parentName="p">{`OTHER`}</inlineCode>{`, describe the circumstances in `}<inlineCode parentName="p">{`otherReason`}</inlineCode>{`.`}</p>
      <p>{`A successful revocation returns `}<inlineCode parentName="p">{`204 No Content`}</inlineCode>{` with an empty body. Revocation is not idempotent: a grant that is already revoked or expired returns `}<inlineCode parentName="p">{`409 CONFLICT`}</inlineCode>{` rather than succeeding again. Revocation is also per grant, so a customer with several grants for the same application needs a call for each `}<inlineCode parentName="p">{`consentId`}</inlineCode>{`.`}</p>
      <Schema method='PUT' route='/fdx/consents/{consentId}/revocation' type='request' mdxType="Schema">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X PUT 'https://production.plaid.com/fdx/consents/<consent_id>/revocation' \\
  -H 'PLAID-CLIENT-ID: <client_id>' \\
  -H 'PLAID-SECRET: <secret>' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "initiator": "DATA_PROVIDER",
    "reason": "USER_ACTION"
  }'
`}</code></pre>
      </Schema>
      <Callout mdxType="Callout">
        <p>{`Revoking a consent grant stops the application from requesting new data. It does not delete data the application has already received; a customer who wants that data deleted has to ask the application directly.`}</p>
      </Callout>
      <h3 {...{
        "id": "retrieve-a-consent-grants-revocation-history"
      }}>{`Retrieve a consent grant's revocation history`}</h3>
      <h4 {...{
        "id": "get-fdxconsentsconsentidrevocation"
      }}><inlineCode parentName="h4">{`GET /fdx/consents/{consentId}/revocation`}</inlineCode></h4>
      <p>{`Use this endpoint to show a customer when and why access ended, and to keep an audit record of who initiated it. Records are returned most recent first, and the array is empty for a grant that has never been revoked.`}</p>
      <Schema method='GET' route='/fdx/consents/{consentId}/revocation' type='request' mdxType="Schema">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X GET 'https://production.plaid.com/fdx/consents/<consent_id>/revocation' \\
  -H 'PLAID-CLIENT-ID: <client_id>' \\
  -H 'PLAID-SECRET: <secret>'
`}</code></pre>
      </Schema>
      <Schema method='GET' route='/fdx/consents/{consentId}/revocation' type='response' mdxType="Schema" />
      <h2 {...{
        "id": "testing-in-sandbox"
      }}>{`Testing in Sandbox`}</h2>
      <p>{`A consent grant is normally created when a customer authorizes an application through your OAuth flow. To exercise these endpoints in Sandbox without a live connection, seed a grant with `}<inlineCode parentName="p">{`/sandbox/fdx/consent/seed`}</inlineCode>{` and then list, retrieve, and revoke it as you would in production.`}</p>
      <p>{`Pass `}<inlineCode parentName="p">{`consent_id`}</inlineCode>{` to control the identifier of the seeded grant, which makes tests deterministic; omit it and Plaid generates one. Seeding fails if a grant with that identifier already exists.`}</p>
      <p>{`Revoking a consent grant is permanent. To test reconnection, seed a second grant for the same `}<inlineCode parentName="p">{`customer_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`application_id`}</inlineCode>{`; it is issued a new `}<inlineCode parentName="p">{`consent_id`}</inlineCode>{`, and the original stays in the customer's history as `}<inlineCode parentName="p">{`REVOKED`}</inlineCode>{`.`}</p>
      <h4 {...{
        "id": "post-sandboxfdxconsentseed"
      }}><inlineCode parentName="h4">{`POST /sandbox/fdx/consent/seed`}</inlineCode></h4>
      <Schema method='POST' route='/sandbox/fdx/consent/seed' type='request' mdxType="Schema">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST 'https://sandbox.plaid.com/sandbox/fdx/consent/seed' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": "<client_id>",
    "secret": "<secret>",
    "customer_id": "consentapi-example-1",
    "application_id": "3f952a96-b6f9-4f09-a1f7-1a474cb128f6"
  }'
`}</code></pre>
      </Schema>
      <Schema method='POST' route='/sandbox/fdx/consent/seed' type='response' mdxType="Schema" />
      <p>{`Plaid provides demo applications to grant consent to when seeding:`}</p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Demo application`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`application_id`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Budget Works`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`c048eab9-a70a-42fe-94e9-35980d0a965c`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Incremental Investing`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`36b78a27-ebbb-417b-a7b7-351734d2f8bd`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`My Peer Payer`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`3f952a96-b6f9-4f09-a1f7-1a474cb128f6`}</inlineCode></td>
          </tr>
        </tbody>
      </table>
      <p>{`Seeded grants are never deleted, so repeatedly seeding and revoking the same `}<inlineCode parentName="p">{`customer_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`application_id`}</inlineCode>{` pair grows that customer's result set from `}<a parentName="p" {...{
          "href": "#list-a-customers-consent-grants"
        }}><inlineCode parentName="a">{`/fdx/consents`}</inlineCode></a>{` indefinitely. Account for this in automated tests.`}</p>
      <h2 {...{
        "id": "errors"
      }}>{`Errors`}</h2>
      <p>{`These endpoints use the FDX error format rather than Plaid's standard `}<a parentName="p" {...{
          "href": "/docs/errors/"
        }}>{`error object`}</a>{`. The body contains `}<inlineCode parentName="p">{`error_type`}</inlineCode>{`, `}<inlineCode parentName="p">{`error_code`}</inlineCode>{`, and `}<inlineCode parentName="p">{`error_message`}</inlineCode>{` only — there is no `}<inlineCode parentName="p">{`display_message`}</inlineCode>{` or `}<inlineCode parentName="p">{`request_id`}</inlineCode>{`. `}<inlineCode parentName="p">{`error_type`}</inlineCode>{` and `}<inlineCode parentName="p">{`error_code`}</inlineCode>{` are stable and safe to branch on; `}<inlineCode parentName="p">{`error_message`}</inlineCode>{` is not.`}</p>
      <Layout.FullWidth>
        <div className="status-error-type-code-table">
          <table>
            <thead parentName="table">
              <tr parentName="thead">
                <th parentName="tr" {...{
                  "align": null
                }}>{`Status`}</th>
                <th parentName="tr" {...{
                  "align": null
                }}>{`error_type`}</th>
                <th parentName="tr" {...{
                  "align": null
                }}>{`error_code`}</th>
                <th parentName="tr" {...{
                  "align": null
                }}>{`Returned when`}</th>
              </tr>
            </thead>
            <tbody parentName="table">
              <tr parentName="tbody">
                <td parentName="tr" {...{
                  "align": null
                }}>{`400`}</td>
                <td parentName="tr" {...{
                  "align": null
                }}><inlineCode parentName="td">{`BAD_REQUEST`}</inlineCode></td>
                <td parentName="tr" {...{
                  "align": null
                }}><inlineCode parentName="td">{`32`}</inlineCode></td>
                <td parentName="tr" {...{
                  "align": null
                }}><inlineCode parentName="td">{`customerId`}</inlineCode>{` is missing when listing grants, or the revocation body cannot be parsed or names an unsupported `}<inlineCode parentName="td">{`reason`}</inlineCode>{` or `}<inlineCode parentName="td">{`initiator`}</inlineCode></td>
              </tr>
              <tr parentName="tbody">
                <td parentName="tr" {...{
                  "align": null
                }}>{`404`}</td>
                <td parentName="tr" {...{
                  "align": null
                }}><inlineCode parentName="td">{`NOT_FOUND`}</inlineCode></td>
                <td parentName="tr" {...{
                  "align": null
                }}><inlineCode parentName="td">{`1107`}</inlineCode></td>
                <td parentName="tr" {...{
                  "align": null
                }}>{`The consent grant does not exist, or belongs to another institution`}</td>
              </tr>
              <tr parentName="tbody">
                <td parentName="tr" {...{
                  "align": null
                }}>{`409`}</td>
                <td parentName="tr" {...{
                  "align": null
                }}><inlineCode parentName="td">{`CONFLICT`}</inlineCode></td>
                <td parentName="tr" {...{
                  "align": null
                }}><inlineCode parentName="td">{`409`}</inlineCode></td>
                <td parentName="tr" {...{
                  "align": null
                }}>{`Revocation was requested for a grant that is already revoked or expired`}</td>
              </tr>
              <tr parentName="tbody">
                <td parentName="tr" {...{
                  "align": null
                }}>{`500`}</td>
                <td parentName="tr" {...{
                  "align": null
                }}><inlineCode parentName="td">{`INTERNAL_SERVER_ERROR`}</inlineCode></td>
                <td parentName="tr" {...{
                  "align": null
                }}><inlineCode parentName="td">{`01`}</inlineCode></td>
                <td parentName="tr" {...{
                  "align": null
                }}>{`An unexpected error occurred`}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Layout.FullWidth>
      <p>{`A `}<inlineCode parentName="p">{`404`}</inlineCode>{` deliberately does not distinguish a grant that does not exist from one belonging to another institution, so you cannot use it to probe for grants you do not own.`}</p>
      <p>{`Because a failure here surfaces inside your own consent portal, treat these responses as internal diagnostics rather than something to pass through to the customer. A lookup that returns `}<inlineCode parentName="p">{`404`}</inlineCode>{` is better rendered as an empty state than as an error.`}</p>
      <h2 {...{
        "id": "migrating-from-permissions-manager-to-the-consent-api"
      }}>{`Migrating from Permissions Manager to the Consent API`}</h2>
      <p>{`The two APIs describe the same underlying consent in different schemas. `}</p>
      <Layout.FullWidth>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`To do this`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Permissions Manager API`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Consent API`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`List a customer's connected applications`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/core-exchange/docs/consent-management/permissions-manager#list-a-customers-connected-applications"
                }}><inlineCode parentName="a">{`/item/application/list`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/core-exchange/docs/consent-management/api#list-a-customers-consent-grants"
                }}><inlineCode parentName="a">{`GET /fdx/consents`}</inlineCode></a></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`Read the details of one authorization`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/core-exchange/docs/consent-management/permissions-manager#list-a-customers-connected-applications"
                }}><inlineCode parentName="a">{`/item/application/list`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/core-exchange/docs/consent-management/api#get-a-single-consent-grant"
                }}><inlineCode parentName="a">{`GET /fdx/consents/{consentId}`}</inlineCode></a></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`Revoke an application's access`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/core-exchange/docs/consent-management/permissions-manager#revoke-an-applications-access"
                }}><inlineCode parentName="a">{`/item/application/unlink`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/core-exchange/docs/consent-management/api#revoke-a-consent-grant"
                }}><inlineCode parentName="a">{`PUT /fdx/consents/{consentId}/revocation`}</inlineCode></a></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`Read revocation history`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/core-exchange/docs/consent-management/permissions-manager#read-consent-activity-and-last-access-times"
                }}><inlineCode parentName="a">{`/item/activity/list`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/core-exchange/docs/consent-management/api#retrieve-a-consent-grants-revocation-history"
                }}><inlineCode parentName="a">{`GET /fdx/consents/{consentId}/revocation`}</inlineCode></a></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`See when each application last read each data type`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/core-exchange/docs/consent-management/permissions-manager#read-consent-activity-and-last-access-times"
                }}><inlineCode parentName="a">{`/item/activity/list`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Not available`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`See connection, disconnection, and data-deletion events`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/core-exchange/docs/consent-management/permissions-manager#read-consent-activity-and-last-access-times"
                }}><inlineCode parentName="a">{`/item/activity/list`}</inlineCode></a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Not available`}</td>
            </tr>
          </tbody>
        </table>
      </Layout.FullWidth>
      <p>{`The two views describe the same underlying consent, and revoking through either one takes effect everywhere.`}</p>
      <h3 {...{
        "id": "customer-identification-differences"
      }}>{`Customer identification differences`}</h3>
      <p>{`Permissions Manager identifies a customer by an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, so every call is preceded by exchanging a customer identifier for one via `}<a parentName="p" {...{
          "href": "/core-exchange/docs/consent-management/permissions-manager#exchange-a-customer-identifier-for-an-access-token"
        }}><inlineCode parentName="a">{`/item/import`}</inlineCode></a>{`. The Consent API drops this step: every endpoint takes your `}<inlineCode parentName="p">{`customerId`}</inlineCode>{` directly, the same persistent identifier you use as the `}<inlineCode parentName="p">{`sub`}</inlineCode>{` claim in your OIDC ID token. If you migrate, you can remove the `}<inlineCode parentName="p">{`/item/import`}</inlineCode>{` call from your integration entirely.`}</p>
      <h3 {...{
        "id": "scope-mapping"
      }}>{`Scope mapping`}</h3>
      <p>{`Permissions Manager reports scopes as `}<inlineCode parentName="p">{`product_access`}</inlineCode>{` keys; the Consent API reports the same access as FDX data clusters. The two taxonomies don't line up one to one — for example, `}<inlineCode parentName="p">{`account_balance_info`}</inlineCode>{` spans both `}<inlineCode parentName="p">{`ACCOUNT_BASIC`}</inlineCode>{` and `}<inlineCode parentName="p">{`BALANCES`}</inlineCode>{`:`}</p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Permissions Manager product_access key`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Consent API data cluster(s)`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`account_balance_info`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ACCOUNT_BASIC`}</inlineCode>{`, `}<inlineCode parentName="td">{`BALANCES`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`contact_info`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`CUSTOMER_CONTACT`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`account_routing_number`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`PAYMENT_SUPPORT`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`transactions`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`TRANSACTIONS`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`credit_loan_info`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`ACCOUNT_DETAILED`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`investments`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`INVESTMENTS`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`bank_statements`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`STATEMENTS`}</inlineCode></td>
          </tr>
        </tbody>
      </table>
      <p>{`This mapping is derived from each field's description in the two schemas, not from a joint spec. Confirm it with your Plaid solutions engineering team before relying on it for scope-gated logic.`}</p>
      <h3 {...{
        "id": "error-handling"
      }}>{`Error handling`}</h3>
      <p>{`Permissions Manager returns Plaid's standard `}<a parentName="p" {...{
          "href": "/docs/errors/"
        }}>{`error object`}</a>{` (`}<inlineCode parentName="p">{`error_type`}</inlineCode>{`, `}<inlineCode parentName="p">{`error_code`}</inlineCode>{`, `}<inlineCode parentName="p">{`error_message`}</inlineCode>{`, `}<inlineCode parentName="p">{`display_message`}</inlineCode>{`, `}<inlineCode parentName="p">{`request_id`}</inlineCode>{`). The Consent API uses the FDX error format instead — only `}<inlineCode parentName="p">{`error_type`}</inlineCode>{`, `}<inlineCode parentName="p">{`error_code`}</inlineCode>{`, and `}<inlineCode parentName="p">{`error_message`}</inlineCode>{`, with no `}<inlineCode parentName="p">{`display_message`}</inlineCode>{` or `}<inlineCode parentName="p">{`request_id`}</inlineCode>{` — so error-handling code written against Permissions Manager will need updating rather than reused as-is. See `}<a parentName="p" {...{
          "href": "#errors"
        }}>{`Errors`}</a>{` above for the full list.`}</p>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 2,
        title: "Overview",
      },
{
        id: "customer-identifiers",
        level: 2,
        title: "Customer identifiers",
      },
{
        id: "data-clusters",
        level: 2,
        title: "Data clusters",
      },
{
        id: "revocation-and-ecosystem-sync",
        level: 2,
        title: "Revocation and ecosystem sync",
      },
{
        id: "webhooks",
        level: 2,
        title: "Webhooks",
      },
{
        id: "authorization_granted",
        level: 3,
        title: "AUTHORIZATION_GRANTED",
      },
{
        id: "consent_revoked",
        level: 3,
        title: "CONSENT_REVOKED",
      },
{
        id: "best-practices",
        level: 2,
        title: "Best practices",
      },
{
        id: "authentication",
        level: 2,
        title: "Authentication",
      },
{
        id: "endpoints",
        level: 2,
        title: "Endpoints",
      },
{
        id: "list-a-customers-consent-grants",
        level: 3,
        title: "List a customer's consent grants",
      },
{
        id: "get-fdxconsents",
        level: 4,
        title: "GET /fdx/consents",
      },
{
        id: "get-a-single-consent-grant",
        level: 3,
        title: "Get a single consent grant",
      },
{
        id: "get-fdxconsentsconsentid",
        level: 4,
        title: "GET /fdx/consents/{consentId}",
      },
{
        id: "revoke-a-consent-grant",
        level: 3,
        title: "Revoke a consent grant",
      },
{
        id: "put-fdxconsentsconsentidrevocation",
        level: 4,
        title: "PUT /fdx/consents/{consentId}/revocation",
      },
{
        id: "retrieve-a-consent-grants-revocation-history",
        level: 3,
        title: "Retrieve a consent grant's revocation history",
      },
{
        id: "get-fdxconsentsconsentidrevocation",
        level: 4,
        title: "GET /fdx/consents/{consentId}/revocation",
      },
{
        id: "testing-in-sandbox",
        level: 2,
        title: "Testing in Sandbox",
      },
{
        id: "post-sandboxfdxconsentseed",
        level: 4,
        title: "POST /sandbox/fdx/consent/seed",
      },
{
        id: "errors",
        level: 2,
        title: "Errors",
      },
{
        id: "migrating-from-permissions-manager-to-the-consent-api",
        level: 2,
        title: "Migrating from Permissions Manager to the Consent API",
      },
{
        id: "customer-identification-differences",
        level: 3,
        title: "Customer identification differences",
      },
{
        id: "scope-mapping",
        level: 3,
        title: "Scope mapping",
      },
{
        id: "error-handling",
        level: 3,
        title: "Error handling",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
